import { useState } from 'react';
import { useSiteData } from '@/contexts/SiteDataContext';
import type { TeamMember } from '@/contexts/SiteDataContext';

export default function TeamSection() {
  const { data, updateTeam } = useSiteData();
  const [team, setTeam] = useState<TeamMember[]>([...data.team]);
  const [saved, setSaved] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const handleSave = () => { updateTeam(team); setSaved(true); setTimeout(() => setSaved(false), 2500); };
  const update = (i: number, field: keyof TeamMember, val: string) => setTeam(team.map((m, idx) => idx === i ? { ...m, [field]: val } : m));
  const add = () => { setTeam([...team, { name: 'New Member', role: 'Role', bio: 'Bio here', imageUrl: '' }]); setExpandedIdx(team.length); };
  const remove = (i: number) => setTeam(team.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground-900 mb-1">Team Members</h2>
          <p className="text-foreground-500 text-sm">Manage team profiles shown on the About page. ({team.length} members)</p>
        </div>
        <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-xl text-sm font-semibold hover:bg-secondary-600 transition-colors whitespace-nowrap">
          <i className="ri-user-add-line"></i> Add Member
        </button>
      </div>

      <div className="space-y-3">
        {team.map((member, i) => (
          <div key={i} className="border border-background-200 rounded-2xl overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 bg-background-50 cursor-pointer" onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}>
              {member.imageUrl ? (
                <img src={member.imageUrl} alt={member.name} className="w-10 h-10 rounded-full object-cover object-top border border-background-200" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-500"></i>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground-900 truncate">{member.name}</p>
                <p className="text-xs text-primary-600">{member.role}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); remove(i); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-600 transition-colors cursor-pointer">
                  <i className="ri-delete-bin-line text-xs"></i>
                </button>
                <i className={`${expandedIdx === i ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
              </div>
            </div>
            {expandedIdx === i && (
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-background-200 bg-white">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Full Name</label>
                  <input type="text" value={member.name} onChange={(e) => update(i, 'name', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Role / Title</label>
                  <input type="text" value={member.role} onChange={(e) => update(i, 'role', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Photo URL</label>
                  <input type="text" value={member.imageUrl} onChange={(e) => update(i, 'imageUrl', e.target.value)} placeholder="https://..." className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Bio</label>
                  <textarea rows={3} value={member.bio} onChange={(e) => update(i, 'bio', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <button onClick={handleSave} className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${saved ? 'bg-emerald-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'}`}>
        <i className={saved ? 'ri-check-line' : 'ri-save-line'}></i>
        {saved ? 'Saved!' : 'Save Team'}
      </button>
    </div>
  );
}